import {
    Component,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Output,
    EventEmitter,
    Input,
} from '@angular/core';
import { Highlightable } from '@angular/cdk/a11y';
import { ENTER, hasModifierKey, SPACE } from '@angular/cdk/keycodes';
import { isUndefined } from '@core/utils/object';

export class OptionSelectionEvent {
    constructor(
        public source: OptionComponent,
        public isUserInput: boolean = false
    ) {}
}

let uniqueId = 1;

@Component({
    selector: 'ihd-option',
    templateUrl: './option.component.html',
    styleUrls: ['./option.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'c-option',
        '[class.is-selected]': 'isSelected',
        '[class.is-active]': 'isActive',
        '[class.is-disabled]': 'disabled',
        '(keydown)': 'handleKeydown($event)',
        '(click)': 'select()',
        '[attr.id]': 'id',
        '[attr.disabled]': 'disabled',
    },
})
export class OptionComponent implements Highlightable {
    @Input()
    value: any;

    /**
     * Any thing that uniquely identifies the value in the list. Could be the value itself incase where the value is a basic data type
     */
    @Input()
    id: string | number;

    @Input()
    displayKey: string;

    @Input()
    customSelect: boolean = false;

    @Input()
    disabled?: boolean;

    @Output()
    readonly onSelectionChange = new EventEmitter<OptionSelectionEvent>();

    public isSelected: boolean = false;
    public isActive: boolean = false;

    constructor(private cdr: ChangeDetectorRef) {}

    selectViaClick(forceValue?: boolean) {
        this.isSelected = !isUndefined(forceValue)
            ? forceValue
            : !this.isSelected;
        this.emitSelectionChange(true);
        this.cdr.markForCheck();
    }

    select() {
        if (!this.isSelected) {
            this.isSelected = true;
            this.cdr.markForCheck();
            this.emitSelectionChange();
        }
    }

    deselect(): void {
        if (this.isSelected) {
            this.isSelected = false;
            this.cdr.markForCheck();
            this.emitSelectionChange();
        }
    }

    // interface: Highlightable
    setActiveStyles() {
        if (!this.isActive) {
            this.isActive = true;
            this.cdr.markForCheck();
        }
    }

    // interface: Highlightable
    setInactiveStyles() {
        if (this.isActive) {
            this.isActive = false;
            this.cdr.markForCheck();
        }
    }

    handleKeydown(event: KeyboardEvent) {
        if (
            (event.keyCode === ENTER || event.keyCode === SPACE) &&
            !hasModifierKey(event)
        ) {
            this.selectViaClick();

            // Prevent the page from scrolling down and form submits.
            event.preventDefault();
        }
    }

    compare(value: any) {
        let isSame: boolean;
        if (typeof value === 'object') {
            isSame = Object.values(value).indexOf(this.id) > -1;
        } else {
            isSame = value === this.id;
        }
        return isSame;
    }

    private emitSelectionChange(isUserInput: boolean = false) {
        this.onSelectionChange.emit(
            new OptionSelectionEvent(this, isUserInput)
        );
    }
}
